import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_TEMPLATES = 'FETCH_TEMPLATES';
export const FETCH_TEMPLATES_SUCCESS = 'FETCH_TEMPLATES_SUCCESS';
export const FETCH_TEMPLATES_FAIL = 'FETCH_TEMPLATES_FAIL';
export const FETCH_TEMPLATE = 'FETCH_TEMPLATE';
export const FETCH_TEMPLATE_SUCCESS = 'FETCH_TEMPLATE_SUCCESS';
export const FETCH_TEMPLATE_FAIL = 'FETCH_TEMPLATE_FAIL';
export const CREATE_TEMPLATE = 'CREATE_TEMPLATE';
export const CREATE_TEMPLATE_SUCCESS = 'CREATE_TEMPLATE_SUCCESS';
export const CREATE_TEMPLATE_FAIL = 'CREATE_TEMPLATE_FAIL';
export const UPDATE_TEMPLATE = 'UPDATE_TEMPLATE';
export const UPDATE_TEMPLATE_SUCCESS = 'UPDATE_TEMPLATE_SUCCESS';
export const UPDATE_TEMPLATE_FAIL = 'UPDATE_TEMPLATE_FAIL';
export const DELETE_TEMPLATE = 'DELETE_TEMPLATE';
export const DELETE_TEMPLATE_SUCCESS = 'DELETE_TEMPLATE_SUCCESS';
export const DELETE_TEMPLATE_FAIL = 'DELETE_TEMPLATE_FAIL';

// Reducer
export default function templatesReducer(state = { data: [], count: 0, loading: 0 }, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_TEMPLATE:
    case FETCH_TEMPLATES:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case FETCH_TEMPLATES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_TEMPLATE_FAIL:
    case FETCH_TEMPLATES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_TEMPLATE_SUCCESS:
    case CREATE_TEMPLATE_SUCCESS: {
      let isPresent = false;
      let newCount = state.count;
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        isPresent = true;
        return {
          ...dataItem,
          ...payload,
        };
      });
      if (!isPresent) {
        newCount += 1;
        newData.push(payload);
      }
      return {
        ...state,
        count: newCount,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case UPDATE_TEMPLATE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_TEMPLATE_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchTemplates(payload) {
  return { type: FETCH_TEMPLATES, payload };
}

export function fetchTemplatesSuccess(payload) {
  return { type: FETCH_TEMPLATES_SUCCESS, payload };
}

export function fetchTemplatesFail(payload) {
  return { type: FETCH_TEMPLATES_FAIL, payload };
}

export function fetchTemplate(payload) {
  return { type: FETCH_TEMPLATE, payload };
}

export function fetchTemplateSuccess(payload) {
  return { type: FETCH_TEMPLATE_SUCCESS, payload };
}

export function fetchTemplateFail(payload) {
  return { type: FETCH_TEMPLATE_FAIL, payload };
}

export function createTemplate(payload) {
  return { type: CREATE_TEMPLATE, payload };
}

export function createTemplateSuccess(payload) {
  return { type: CREATE_TEMPLATE_SUCCESS, payload };
}

export function createTemplateFail(payload) {
  return { type: CREATE_TEMPLATE_FAIL, payload };
}

export function updateTemplate(payload) {
  return { type: UPDATE_TEMPLATE, payload };
}

export function updateTemplateSuccess(payload) {
  return { type: UPDATE_TEMPLATE_SUCCESS, payload };
}

export function updateTemplateFail(payload) {
  return { type: UPDATE_TEMPLATE_FAIL, payload };
}

export function deleteTemplate(payload) {
  return { type: DELETE_TEMPLATE, payload };
}

export function deleteTemplateSuccess(payload) {
  return { type: DELETE_TEMPLATE_SUCCESS, payload };
}

export function deleteTemplateFail(payload) {
  return { type: DELETE_TEMPLATE_FAIL, payload };
}

// Requests

let abort;

export function requestFetchTemplates({ limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = limit !== undefined && offset !== undefined
    ? abortableCallApi(`settings/settings_template/?limit=${limit}&offset=${offset}`)
    : abortableCallApi('settings/settings_template/');
  abort = request.abort;
  return request.res;
}

export function requestSearchTemplates({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('settings/settings_template/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestFetchTemplate(payload) {
  return callApi(`settings/settings_template/?id=${payload}`);
}

export function requestCreateTemplate(payload) {
  return callApi('settings/settings_template/', 'POST', payload);
}

export function requestUpdateTemplate(payload) {
  return callApi('settings/settings_template/', 'PATCH', payload);
}

export function requestDeleteTemplate(payload) {
  return callApi(`settings/settings_template/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchTemplatesSaga(action) {
  const { triggerLoading, filters, push } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchTemplates, action.payload)
      : yield call(requestFetchTemplates, action.payload);
    if (res.count === 0) {
      yield put(fetchTemplatesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else if (push) {
      yield put(fetchTemplatesSuccess({
        push: true,
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else {
      yield put(fetchTemplatesSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchTemplatesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* fetchTemplateSaga(action) {
  const { triggerLoading } = action.payload;
  try {
    const res = yield call(requestFetchTemplate, action.payload.ID);
    if (res.data.ID) {
      yield put(fetchTemplateSuccess({
        ...res.data,
        triggerLoading,
      }));
    } else {
      yield put(fetchTemplateFail({
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchTemplateFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createTemplateSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateTemplate, values);
    yield put(createTemplateSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Template successfully created',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createTemplateFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateTemplateSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateTemplate, values);
    yield put(updateTemplateSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Template successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateTemplateFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteTemplateSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteTemplate, ID);
    yield put(deleteTemplateSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(deleteTemplateFail(err.data));
  }
}

export function* templatesSaga() {
  yield takeLatest(FETCH_TEMPLATES, fetchTemplatesSaga);
  yield takeLatest(FETCH_TEMPLATE, fetchTemplateSaga);
  yield takeEvery(CREATE_TEMPLATE, createTemplateSaga);
  yield takeEvery(UPDATE_TEMPLATE, updateTemplateSaga);
  yield takeEvery(DELETE_TEMPLATE, deleteTemplateSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/templates.js